import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
	Box,
	Typography,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	LinearProgress,
	List,
	ListItem,
	ListItemText,
	IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import { useResolutionScale } from '../../hooks';
import { useMessageContextManager } from '../../components/MessageOverlay/MessageOverlay';
import { StyledTable } from '../../components';
import TableActionBar, { GradientButton } from '../../components/TableActionBar/TableActionBar';
import { OPERATIONS } from '../../constants';
import { updateOperations } from '../../redux/actionCreators/operations';
import { fetchSFLTVehicles, receiveSFLTVehicle, completeSFLT } from '../../redux/actionCreators/sflt';
import styles from './SFLT.module.scss';

const card = {
	border: '1px solid',
	borderColor: 'divider',
	borderRadius: '8px',
	backgroundColor: 'background.paper',
	boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
};

const monoLabel = {
	fontSize: 11,
	fontFamily: "'IBM Plex Mono', monospace",
	fontWeight: 600,
	letterSpacing: '0.08em',
	textTransform: 'uppercase',
	color: 'text.secondary',
};

const Badge = ({ color = 'amber', children, dot = true }) => {
	const colors = {
		amber: { bg: 'rgba(245,158,11,0.12)', text: '#f59e0b', border: 'rgba(245,158,11,0.3)' },
		green: { bg: 'rgba(34,197,94,0.12)', text: '#22c55e', border: 'rgba(34,197,94,0.3)' },
		red: { bg: 'rgba(239,68,68,0.1)', text: '#ef4444', border: 'rgba(239,68,68,0.2)' },
		gray: { bg: 'rgba(128,128,128,0.1)', text: 'text.secondary', border: 'divider' },
	};
	const c = colors[color] || colors.amber;
	return (
		<Box sx={{
			display: 'inline-flex', alignItems: 'center', gap: '5px',
			px: '9px', py: '3px', borderRadius: '99px',
			fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 500,
			backgroundColor: c.bg,
			color: c.text === 'text.secondary' ? 'text.secondary' : c.text,
			border: c.border === 'divider' ? '1px solid' : `1px solid ${c.border}`,
			borderColor: c.border === 'divider' ? 'divider' : undefined,
			whiteSpace: 'nowrap', flexShrink: 0,
		}}>
			{dot && <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: 'currentColor', flexShrink: 0 }} />}
			{children}
		</Box>
	);
};

const isEligible = (vehicle) => !!vehicle.wtclCompleted && !!vehicle.wcpaCompleted;
const idOf = (vehicle) => vehicle.asn || vehicle.vin;

const BATCH_STATUS_META = {
	pending: { label: 'QUEUED', color: 'gray' },
	processing: { label: 'PROCESSING', color: 'amber' },
	success: { label: 'DONE', color: 'green' },
	failed: { label: 'FAILED', color: 'red' },
};

const getTableColumns = () => [
	{ field: 'lotNo', title: 'LOT NO.', width: 140, sortable: true },
	{ field: 'model', title: 'MODEL', width: 220, sortable: true },
	{ field: 'vin', title: 'VIN', width: 240, sortable: true },
	{
		field: 'wtclCompleted',
		title: 'WTCL',
		width: 120,
		align: 'center',
		render: (row) => (
			<Badge color={row.wtclCompleted ? 'green' : 'red'} dot>
				{row.wtclCompleted ? 'DONE' : 'PENDING'}
			</Badge>
		),
	},
	{
		field: 'wcpaCompleted',
		title: 'WCPA',
		width: 120,
		align: 'center',
		render: (row) => (
			<Badge color={row.wcpaCompleted ? 'green' : 'red'} dot>
				{row.wcpaCompleted ? 'DONE' : 'PENDING'}
			</Badge>
		),
	},
];

const SFLT = () => {
	const { scaleValue } = useResolutionScale();
	const { showMessageOverlay } = useMessageContextManager();
	const dispatch = useDispatch();

	const [vehicles, setVehicles] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selectedIds, setSelectedIds] = useState([]);
	const [tableColumns, setTableColumns] = useState(() => getTableColumns());
	const [tableFilters, setTableFilters] = useState({});

	// Batch SFLT dialog state
	const [batchDialogOpen, setBatchDialogOpen] = useState(false);
	const [batchItems, setBatchItems] = useState([]);
	const [batchProcessing, setBatchProcessing] = useState(false);

	// Mutable, non-rendering state used to drive the sequential processing loop.
	const queueRef = useRef([]); // ordered list of ids still to (re)process
	const itemsMetaRef = useRef({}); // id -> { asn, vin, lotNo, model }
	const cancelledRef = useRef(false); // set true when dialog is closed mid-run
	const processingRef = useRef(false); // guards against overlapping run loops

	// MOCK DATA — comment out loadVehicles() call in useEffect and uncomment this block to test UI
	// const MOCK_VEHICLES = [
	// 	{ asn: '11301014-202605-0013', lotNo: 'LOT001', model: 'Defender 90', vin: 'ASABA0220JRKJ95AS', wtclCompleted: true,  wcpaCompleted: true  },
	// 	{ asn: 'ASN002', lotNo: 'LOT002', model: 'Defender 110', vin: 'SAL1A2AW7TL942202', wtclCompleted: true,  wcpaCompleted: false },
	// 	{ asn: 'ASN003', lotNo: 'LOT003', model: 'Range Rover', vin: 'SAL1A2AW7TL942203', wtclCompleted: false, wcpaCompleted: false },
	// 	{ asn: 'ASN004', lotNo: 'LOT004', model: 'Discovery',   vin: 'SAL1A2AW7TL942204', wtclCompleted: true,  wcpaCompleted: true  },
	// ];

	const loadVehicles = useCallback(() => {
		setLoading(true);
		// To test with mock data: comment the dispatch block below and uncomment these two lines:
		// setVehicles(MOCK_VEHICLES);
		// setLoading(false);
		//.  ------

		dispatch(
			fetchSFLTVehicles(
				(data) => {
					setVehicles(data || []);
					setLoading(false);
				},
				() => {
					showMessageOverlay({ contextText: 'Failed to load SFLT vehicles', error: true });
					setLoading(false);
				},
			),
		);
	}, [dispatch]);

	useEffect(() => {
		loadVehicles();
		dispatch(updateOperations({ currentOperation: OPERATIONS.SFLT }));
	}, []);

	const toggleRow = (vehicle) => {
		if (!isEligible(vehicle)) return;
		const id = idOf(vehicle);
		setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
	};

	const selectedVehicles = vehicles.filter((v) => selectedIds.includes(idOf(v)));
	const tableRows = vehicles.map((vehicle) => ({ ...vehicle, id: idOf(vehicle) }));

	const handleTableRowClick = (id, row) => {
		toggleRow(row);
	};

	// Multi-select: keep only ids that belong to eligible vehicles, ignore the rest silently.
	const handleSelectionChange = (nextSelectedIds) => {
		const eligibleIds = nextSelectedIds.filter((id) => {
			const vehicle = vehicles.find((v) => idOf(v) === id);
			return vehicle && isEligible(vehicle);
		});
		setSelectedIds(eligibleIds);
	};

	const processBatchItem = (meta) =>
		new Promise((resolve, reject) => {
			dispatch(
				receiveSFLTVehicle(
					meta.vin,
					() => {
						dispatch(
							completeSFLT(
								meta.asn,
								() => resolve(),
								(err) => reject(err),
							),
						);
					},
					(err) => reject(err), // receive failed -> reject now, completeSFLT is never touched
				),
			);
		});

	const updateBatchItem = (id, patch) => {
		setBatchItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
	};

	// Sequentially drains queueRef. A failed VIN is automatically re-queued once for a single
	// retry; if it fails again it's marked 'failed' permanently and stays on the SFLT screen.
	const runBatchQueue = async () => {
		if (processingRef.current) return;
		processingRef.current = true;
		setBatchProcessing(true);

		while (queueRef.current.length > 0 && !cancelledRef.current) {
			const id = queueRef.current.shift();
			const meta = itemsMetaRef.current[id];
			if (!meta) continue;

			updateBatchItem(id, { status: 'processing', errorMessage: null });

			try {
				await processBatchItem(meta);

				if (cancelledRef.current) break;

				updateBatchItem(id, { status: 'success', errorMessage: null });
				// Business rule: successfully processed VINs are removed from the SFLT screen.
				setVehicles((prev) => prev.filter((v) => idOf(v) !== id));
			} catch (err) {
				if (cancelledRef.current) break;

				const message = err?.response?.data?.message || 'An error occurred';
				// No automatic retry — a failed VIN is marked failed immediately and only
				// re-queued if the operator explicitly clicks retry (see handleRetryItem).
				updateBatchItem(id, { status: 'failed', errorMessage: message });
			}
		}

		processingRef.current = false;
		setBatchProcessing(false);
	};

	const handleOpenBatchDialog = () => {
		if (!selectedVehicles.length) return;

		const items = selectedVehicles.map((v) => ({
			id: idOf(v),
			asn: v.asn,
			vin: v.vin,
			lotNo: v.lotNo,
			model: v.model,
			status: 'pending',
			errorMessage: null,
		}));

		itemsMetaRef.current = {};
		items.forEach((item) => {
			itemsMetaRef.current[item.id] = item;
		});
		queueRef.current = items.map((item) => item.id);
		cancelledRef.current = false;

		setBatchItems(items);
		setSelectedIds([]);
		setBatchDialogOpen(true);
		runBatchQueue();
	};

	const handleRetryItem = (id) => {
		if (!itemsMetaRef.current[id]) return;
		updateBatchItem(id, { status: 'pending', errorMessage: null });
		queueRef.current.push(id);
		runBatchQueue();
	};

	const handleCloseBatchDialog = () => {
		cancelledRef.current = true;
		setBatchDialogOpen(false);
		loadVehicles();
	};

	const totalBatchCount = batchItems.length;
	const successBatchCount = batchItems.filter((item) => item.status === 'success').length;
	const failedBatchCount = batchItems.filter((item) => item.status === 'failed').length;
	const doneBatchCount = successBatchCount + failedBatchCount;
	const batchProgressPct = totalBatchCount ? Math.round((doneBatchCount / totalBatchCount) * 100) : 0;
	const batchComplete = !batchProcessing && totalBatchCount > 0 && doneBatchCount === totalBatchCount;
	const currentProcessingItem = batchItems.find((item) => item.status === 'processing') || null;

	return (
		<Box className={styles.sfltPage}>
			{/* Stats bar */}
			<Box
				sx={{
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					...card,
					mb: 2.5,
					width: '100%',
					boxSizing: 'border-box',
					overflow: 'hidden',
					flexShrink: 0,
				}}
			>
				{[
					{ label: 'Total Vehicles', value: loading ? '—' : vehicles.length },
					{ label: 'Eligible for SFLT', value: loading ? '—' : vehicles.filter(isEligible).length },
				].map(({ label, value }, i, arr) => (
					<Box
						key={label}
						sx={{
							flex: 1,
							p: '16px 20px',
							borderRight: i < arr.length - 1 ? '1px solid' : 'none',
							borderBottom: i < arr.length - 1 ? { xs: '1px solid', sm: 'none' } : 'none',
							borderColor: 'divider',
							minHeight: 84,
							boxSizing: 'border-box',
						}}
					>
						<Typography sx={{ ...monoLabel, mb: 0.5 }}>{label}</Typography>
						<Typography sx={{ fontSize: 24, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1.2 }}>
							{value}
						</Typography>
					</Box>
				))}
			</Box>

			{/* Table */}
			<Box sx={{
				mb: 2.5,
				flex: 1,
				minHeight: 0,
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden',
			}}>
				<TableActionBar
					showColumns={true}
					showFilter={true}
					rows={tableRows}
					columns={tableColumns}
					defaultColumns={getTableColumns()}
					onColumnChange={setTableColumns}
					onFilterChange={setTableFilters}
				/>
				<Box sx={{
					...card,
					flex: 1,
					minHeight: 0,
					overflow: 'hidden',
				}}>
					{loading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
							<CircularProgress sx={{ color: '#2196F3' }} />
						</Box>
					) : vehicles.length === 0 ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
							<Typography sx={{ color: 'text.secondary', fontFamily: "'IBM Plex Mono', monospace", fontSize: 13 }}>
								No vehicles available for SFLT
							</Typography>
						</Box>
					) : (
						<StyledTable
							rows={tableRows}
							columns={tableColumns}
							filters={tableFilters}
							elevated={false}
							showCheckBox={true}
							selectall={true}
							selectedRows={selectedIds}
							onSelectionChange={handleSelectionChange}
							onHandleRowClick={handleTableRowClick}
							sx={{ height: '100%' }}
							rowStyle={(row) => ({
								backgroundColor: selectedIds.includes(row.id) ? 'rgba(33,150,243,0.08)' : undefined,
								'&:hover': {
									backgroundColor: selectedIds.includes(row.id) ? 'rgba(33,150,243,0.1)' : undefined,
								},
							})}
						/>
					)}
				</Box>
			</Box>

			{/* Footer */}
			<Box className={styles.footer}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 2.5, }}>
					<Typography sx={{ fontSize: scaleValue(13), color: 'text.secondary', fontFamily: "'IBM Plex Mono', monospace" }}>
						{selectedVehicles.length
							? <>
								<Box component="span" sx={{ color: '#2196F3', fontWeight: 700 }}>{selectedVehicles.length}</Box>
								{selectedVehicles.length === 1 ? ' vehicle selected' : ' vehicles selected'}
							</>
							: 'Select one or more vehicles with WTCL & WCPA completed to mark SFLT'}
					</Typography>
					<GradientButton
						onClick={handleOpenBatchDialog}
						gradient="linear-gradient(90deg, #2196F3 0%, #21CCE1 100%)"
						sx={{ height: 56, px: 8, borderRadius: '12px' }}
						disabled={!selectedVehicles.length || batchProcessing}
					>
						Mark SFLT{selectedVehicles.length > 1 ? ` (${selectedVehicles.length})` : ''}
					</GradientButton>
				</Box>
			</Box>

			{/* Batch SFLT processing dialog */}
			<Dialog open={batchDialogOpen} onClose={handleCloseBatchDialog} maxWidth="sm" fullWidth>
				<DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
					<Typography sx={{ fontWeight: 700, fontSize: 16 }}>Batch SFLT Processing</Typography>
					<IconButton size="small" onClick={handleCloseBatchDialog}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Box sx={{ mb: 2 }}>
						<Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
							<Typography sx={{ fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", color: 'text.secondary' }}>
								{currentProcessingItem
									? `Processing ${currentProcessingItem.vin}…`
									: batchComplete
										? 'Batch complete'
										: 'Preparing…'}
							</Typography>
							<Typography sx={{ fontSize: 12, fontFamily: "'IBM Plex Mono', monospace", color: 'text.secondary' }}>
								{doneBatchCount}/{totalBatchCount}
							</Typography>
						</Box>
						<LinearProgress variant="determinate" value={batchProgressPct} sx={{ height: 6, borderRadius: 3 }} />
					</Box>

					<Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
						<Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
							Total: <b>{totalBatchCount}</b>
						</Typography>
						<Typography sx={{ fontSize: 12, color: '#22c55e' }}>
							Success: <b>{successBatchCount}</b>
						</Typography>
						<Typography sx={{ fontSize: 12, color: '#ef4444' }}>
							Failed: <b>{failedBatchCount}</b>
						</Typography>
					</Box>

					<List dense sx={{ maxHeight: 360, overflowY: 'auto' }}>
						{batchItems.map((item) => {
							const meta = BATCH_STATUS_META[item.status] || BATCH_STATUS_META.pending;
							return (
								<ListItem
									key={item.id}
									sx={{ ...card, mb: 1, py: 1 }}
									secondaryAction={
										item.status === 'failed' ? (
											<IconButton size="small" onClick={() => handleRetryItem(item.id)} title="Retry">
												<ReplayIcon fontSize="small" />
											</IconButton>
										) : item.status === 'processing' ? (
											<CircularProgress size={16} sx={{ color: '#f59e0b' }} />
										) : null
									}
								>
									<ListItemText
										primary={`VIN: ${item.vin} · Lot No: ${item.lotNo || '—'}`}
										secondary={item.status === 'failed' ? `Error: ${item.errorMessage}` : `Model: ${item.model}`}
										primaryTypographyProps={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 13 }}
										secondaryTypographyProps={{
											fontSize: 11,
											color: item.status === 'failed' ? '#ef4444' : 'text.secondary',
										}}
									/>
									<Box sx={{ mr: 5 }}>
										<Badge color={meta.color}>{meta.label}</Badge>
									</Box>
								</ListItem>
							);
						})}
					</List>
				</DialogContent>

				<DialogActions>
					<Button onClick={handleCloseBatchDialog} variant={batchComplete ? 'contained' : 'text'}>
						{batchComplete ? 'Done' : 'Close'}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default SFLT;